<template>
  <ArtboardFrame>
    <h1 class="heading">{{ t.landingHeading }}</h1>

    <p class="body">
      <span v-for="line in t.landingBody" :key="line" class="body__line">{{ line }}</span>
    </p>

    <img class="tools" :src="toolsSrc" alt="Microsoft Copilot, Claude, ChatGPT, Notion">
    <p class="tools__caption">{{ t.toolsCaption }}</p>

    <p class="subscription">{{ t.subscriptionLabel }}</p>
    <p class="price">{{ t.priceLine }}</p>

    <CtaButton :label="t.subscribeCta" :disabled="submitting" @click="onSubscribeClicked"/>

    <PageFooter/>
  </ArtboardFrame>
</template>

<script>
import {mapGetters, mapState} from "vuex";
import ArtboardFrame from "@/components/ArtboardFrame.vue";
import CtaButton from "@/components/CtaButton.vue";
import PageFooter from "@/components/PageFooter.vue";
import getCampaignParametersFromUrl from "@/functions/getCampaignParametersFromUrl";
import {buildTrxId} from "@/functions/buildTrxId";
import {requestAisWapLink} from "@/functions/requestAisWapLink";
import {triggerGTMEvent} from "@/functions/addGTM";

export default {
  name: 'LandingPage',
  components: {ArtboardFrame, CtaButton, PageFooter},
  data() {
    return {
      submitting: false
    }
  },
  computed: {
    ...mapState(['brandConfig']),
    ...mapGetters(['t']),
    assetBrand() {
      return this.brandConfig.assetBrand || this.brandConfig.brand;
    },
    toolsSrc() {
      return require(`@/brand/${this.assetBrand}/assets/ai-icons.png`);
    }
  },
  methods: {
    async onSubscribeClicked() {
      if (this.submitting) return;
      this.submitting = true;

      try {
        /* One transaction_id for both sinks: GTM for marketing reporting, and the service,
           which parks it against this click's spsID so the subscription AIS confirm later
           can be attributed back to the campaign that produced it. */
        const campaignParameters = getCampaignParametersFromUrl();
        const transactionId = buildTrxId(campaignParameters);

        triggerGTMEvent('subscribe_button_clicked', {transaction_id: transactionId});

        window.location.href = await requestAisWapLink(transactionId);
      } catch (error) {
        console.error('Could not start the AIS subscription flow', error);
        this.submitting = false;
        this.$router.push({name: 'Error'});
      }
    }
  },
  mounted() {
    triggerGTMEvent('landing_page_view');
  }
}
</script>

<style scoped>
.heading {
  position: absolute;
  top: calc(330.1 * var(--u));
  left: 0;
  right: 0;
  margin: 0;
  font-size: calc(20 * var(--u));
  line-height: 1;
  font-weight: 700;
  text-align: center;
  color: var(--slate);
}

.body {
  position: absolute;
  top: calc(368.6 * var(--u));
  left: 0;
  right: 0;
  margin: 0;
  font-size: calc(11.9 * var(--u));
  line-height: calc(18 * var(--u));
  font-weight: 600;
  text-align: center;
  color: var(--body-grey);
}

.body__line {
  display: block;
}

.tools {
  position: absolute;
  top: calc(458 * var(--u));
  left: calc(94.5 * var(--u));
  width: calc(186 * var(--u));
  height: calc(32 * var(--u));
}

.tools__caption {
  position: absolute;
  top: calc(502.7 * var(--u));
  left: 0;
  right: 0;
  margin: 0;
  font-size: calc(11.9 * var(--u));
  line-height: 1;
  font-weight: 500;
  text-align: center;
  color: var(--body-grey);
}

.subscription {
  position: absolute;
  top: calc(553.9 * var(--u));
  left: 0;
  right: 0;
  margin: 0;
  font-size: calc(12 * var(--u));
  line-height: 1;
  font-weight: 700;
  letter-spacing: calc(0.4 * var(--u));
  text-align: center;
  color: var(--slate);
}

.price {
  position: absolute;
  top: calc(587 * var(--u));
  left: 0;
  right: 0;
  margin: 0;
  font-size: calc(11.95 * var(--u));
  line-height: 1;
  font-weight: 500;
  text-align: center;
  color: var(--muted-grey);
}
</style>
